import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, CalendarCheck } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";

async function downloadSlip(slip) {
  try {
    const r = await api.get(`/salary-slips/${slip.id}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = slip.name || `salary-slip-${slip.period_label}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(apiError(e));
  }
}

export default function WrmMyPay() {
  const [slips, setSlips] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/wrm/pay/salary-slips"), api.get("/wrm/pay/attendance")])
      .then(([s, a]) => { setSlips(s.data); setAttendance(a.data); })
      .catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <AppLayout title="My Pay"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!slips || !attendance) return <AppLayout title="My Pay"><PageLoader /></AppLayout>;

  return (
    <AppLayout title="My Pay" subtitle="Your salary slips and monthly attendance records. Visible only to you.">
      <div className="fade-up space-y-6">
        <div className="glass-card overflow-hidden" data-testid="mypay-slips-card">
          <div className="px-6 pt-5 pb-4 flex items-center gap-3">
            <FileText size={18} className="text-emerald-400" weight="duotone" />
            <div>
              <h3 className="font-heading font-semibold text-white">Salary Slips</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">Monthly payslips uploaded by KMC Admin</p>
            </div>
          </div>
          {slips.length === 0 ? <div className="px-6 pb-6"><EmptyState message="No salary slips available yet." /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="mypay-slips-table">
                <thead>
                  <tr className="border-y border-white/[0.06]">
                    <th className={thCls}>Month</th>
                    <th className={thCls}>Document</th>
                    <th className={thCls}>Uploaded</th>
                    <th className={thCls}>Remarks</th>
                    <th className={`${thCls} text-right`}>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {slips.map((s) => (
                    <tr key={s.id} className={rowCls} data-testid={`mypay-slip-row-${s.id}`}>
                      <td className={`${tdCls} font-medium text-white`}>{s.period_label}</td>
                      <td className={`${tdCls} text-xs text-zinc-400`}>
                        {s.name}
                        <span className="text-zinc-600"> · {s.size_kb} KB</span>
                      </td>
                      <td className={`${tdCls} text-xs text-zinc-500 tnum whitespace-nowrap`}>{fmtDate(s.uploaded_at)}</td>
                      <td className={`${tdCls} text-xs text-zinc-500`}>{s.remarks || "—"}</td>
                      <td className={`${tdCls} text-right`}>
                        <button onClick={() => downloadSlip(s)} data-testid={`mypay-slip-download-${s.id}`}
                          className="h-8 w-8 rounded-lg border border-white/10 inline-flex items-center justify-center text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors duration-200">
                          <Download size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="glass-card overflow-hidden" data-testid="mypay-attendance-card">
          <div className="px-6 pt-5 pb-4 flex items-center gap-3">
            <CalendarCheck size={18} className="text-emerald-400" weight="duotone" />
            <div>
              <h3 className="font-heading font-semibold text-white">Attendance</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">Monthly attendance maintained by KMC Admin</p>
            </div>
          </div>
          {attendance.length === 0 ? <div className="px-6 pb-6"><EmptyState message="No attendance records available yet." /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="mypay-attendance-table">
                <thead>
                  <tr className="border-y border-white/[0.06]">
                    <th className={thCls}>Month</th>
                    <th className={`${thCls} text-right`}>Working Days</th>
                    <th className={`${thCls} text-right`}>Present</th>
                    <th className={`${thCls} text-right`}>Absent</th>
                    <th className={`${thCls} text-right`}>Approved Leave</th>
                    <th className={`${thCls} text-right`}>Attendance</th>
                    <th className={thCls}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((a) => (
                    <tr key={a.id} className={rowCls} data-testid={`mypay-att-row-${a.id}`}>
                      <td className={`${tdCls} font-medium text-white`}>{a.period_label}</td>
                      <td className={`${tdCls} text-right tnum text-zinc-300`}>{a.working_days}</td>
                      <td className={`${tdCls} text-right tnum text-emerald-300`}>{a.present}</td>
                      <td className={`${tdCls} text-right tnum text-red-300`}>{a.absent}</td>
                      <td className={`${tdCls} text-right tnum text-amber-300`}>{a.approved_leave}</td>
                      <td className={`${tdCls} text-right tnum font-semibold text-white`}
                        data-testid={`mypay-att-pct-${a.id}`}>{a.attendance_pct}%</td>
                      <td className={`${tdCls} text-xs text-zinc-500`}>{a.remarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
